import React, { memo, useEffect, useState } from 'react';

import { useAppSelector } from '../../../store/hooks';
import { reduceFunc } from '../../../utils/array';
import Font from '../Font';
import Icon from '../Icon';
import {
  AnchoredLeft,
  AnchoredRight,
  CurrentValueWrapper,
  SliderStructure,
  StyledButton,
  StyledMark,
  StyledSlider,
  StyledThumb,
  StyledThumbCursor,
  StyledTrack,
  TitleWrapper
} from './styles';

export type SliderProps = {
  buttonName?: string;
  extraText?: string;
  formatter?: (value: number) => number | string;
  hideTickmarks?: boolean;
  initialValue?: number;
  isUnitMeasureVisible?: boolean;
  labelName: string;
  onChange: (value: number) => void;
  tickmarks: number[];
  unitMeasure?: string;
};

const Thumb = (props: any) => (
  <StyledThumb {...props}>
    <StyledThumbCursor />
  </StyledThumb>
);
const Mark = (props: any) => <StyledMark {...props} />;
const Track = (props: any, { index }: any) => (
  <StyledTrack {...props} index={index} />
);

const concatValueToShow = (
  formattedValue: number | string,
  unitMeasure?: string
) => {
  const um = unitMeasure ? ` ${unitMeasure}` : '';
  return `${formattedValue}${um}`;
};

const findTickmark = (
  tickmarks: number[],
  min: number,
  max: number,
  currentValue: number
) => {
  const indexCurrent = tickmarks.findIndex((elm) => elm === currentValue);
  const previous = indexCurrent === 0 ? min : tickmarks[indexCurrent - 1];
  const next =
    indexCurrent === tickmarks.length - 1 ? max : tickmarks[indexCurrent + 1];
  return { next, previous };
};

const findCurrentValue = (initialValue: number, tickmarks: number[]) => {
  if (tickmarks.includes(initialValue)) {
    return initialValue;
  }
  return tickmarks[0];
};

const SliderInput: React.FC<SliderProps> = memo(
  ({
    buttonName,
    extraText,
    formatter = (val: any) => val,
    hideTickmarks = false,
    initialValue,
    isUnitMeasureVisible,
    labelName,
    onChange,
    tickmarks,
    unitMeasure
  }) => {
    const { widgetType } = useAppSelector((state) => state.app);
    const min = tickmarks[0];
    const max = tickmarks[tickmarks.length - 1];

    const [currentValue, setCurrentValue] = useState(0);
    const [formattedValue, setFormattedValue] = useState('');

    const handleAfterChange = (value: number) => {
      const closestStep = tickmarks.reduce(reduceFunc(value));
      setCurrentValue(closestStep);
      onChange(closestStep);
    };

    const handleChange = (value: number) => {
      const closestStep = tickmarks.reduce(reduceFunc(value));
      setCurrentValue(closestStep);
    };

    const handleCircleClick = (value: number) => {
      setCurrentValue(value);
      onChange(value);
    };

    const findPrevNext = (increment: boolean) => {
      const { next, previous } = findTickmark(
        tickmarks,
        min,
        max,
        currentValue
      );
      const clickValue = increment ? next : previous;
      handleCircleClick(clickValue);
    };

    useEffect(() => {
      if (initialValue && tickmarks && tickmarks.length > 0) {
        setCurrentValue(findCurrentValue(initialValue, tickmarks));
      }
    }, [initialValue]);

    useEffect(() => {
      setFormattedValue(
        concatValueToShow(formatter(currentValue), unitMeasure)
      );
    }, [currentValue, unitMeasure]);

    const unitMeasureBelow = isUnitMeasureVisible ? unitMeasure : '';

    return (
      <SliderStructure>
        <TitleWrapper>
          <Font
            variant={widgetType === 'modal' ? 'bodyRegular' : 'bodyLRegular'}
          >
            {`${labelName}${extraText ?? ''}`}
          </Font>
        </TitleWrapper>
        <CurrentValueWrapper>
          <Font variant={widgetType === 'modal' ? 'bodyBold' : 'bodyLBold'}>
            {formattedValue}
          </Font>
        </CurrentValueWrapper>
        <StyledButton
          data-testid="less_button"
          name={`${buttonName}-minus`}
          onClick={() => findPrevNext(false)}
          type="button"
        >
          <Icon iconColor="white" iconName="InterfaceLess" iconSize="xs" />
        </StyledButton>
        <StyledSlider
          marks={tickmarks}
          max={max}
          min={min}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onAfterChange={(value, index) =>
            typeof value === 'number' ? handleAfterChange(value) : null
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onChange={(value, index) =>
            typeof value === 'number' ? handleChange(value) : null
          }
          renderMark={hideTickmarks ? undefined : Mark}
          renderThumb={Thumb}
          renderTrack={Track}
          value={currentValue}
        />

        <StyledButton
          data-testid="add_button"
          name={`${buttonName}-plus`}
          onClick={() => findPrevNext(true)}
          type="button"
        >
          <Icon iconColor="white" iconName="InterfaceAdd" iconSize="xs" />
        </StyledButton>

        <AnchoredLeft>
          <Font variant="bodyRegular">
            {formatter(min)} {unitMeasureBelow}
          </Font>
        </AnchoredLeft>
        <AnchoredRight>
          <Font variant="bodyRegular">
            {formatter(max)} {unitMeasureBelow}
          </Font>
        </AnchoredRight>
      </SliderStructure>
    );
  }
);

export default SliderInput;
